import React from 'react';
import {
  formatMoneyFromMinor,
  sketchplanChargeSummary,
  sketchplanPriceTableRows,
  sketchplanPricingCopy,
  SKETCHPLAN_COMMERCIAL_RULE_SHORT,
  SKETCHPLAN_PRICING_COPY,
} from '@rehub/pricing';
import CurrencyToggle from './CurrencyToggle';
import { useSketchplanCurrency } from './useSketchplanCurrency';

interface DirectCardPricingTableProps {
  onOrder?: () => void;
}

const DirectCardPricingTable: React.FC<DirectCardPricingTableProps> = ({ onOrder }) => {
  const [currency, setCurrency] = useSketchplanCurrency();
  const copy = sketchplanPricingCopy(currency);
  const rows = sketchplanPriceTableRows(currency);

  const cell = (minor: number | null, increment: boolean) => {
    if (minor == null) return 'Request a quote';
    const amount = formatMoneyFromMinor(minor, currency);
    return increment ? `+${amount}` : amount;
  };

  return (
    <section className="text-left">
      <div className="p-6 bg-slate-900/70 backdrop-blur-md border border-slate-700/80 rounded-2xl">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-white mb-1">{copy.headline}</h2>
            <p className="text-sm text-slate-400">{copy.supporting} {copy.fromSize}</p>
          </div>
          <CurrencyToggle value={currency} onChange={setCurrency} />
        </div>

        <div className="rounded-xl border border-slate-700/60 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-800/70 text-xs uppercase tracking-wide text-slate-300">
              <tr>
                <th scope="col" className="px-4 py-3 text-left font-semibold">Property size</th>
                <th scope="col" className="px-4 py-3 text-right font-semibold">Residential</th>
                <th scope="col" className="px-4 py-3 text-right font-semibold">
                  Commercial <span className="normal-case text-sky-300">({SKETCHPLAN_COMMERCIAL_RULE_SHORT})</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={row.key} className={i % 2 ? 'bg-slate-800/20' : ''}>
                  <th scope="row" className="px-4 py-3 text-left font-normal text-slate-300">{row.label}</th>
                  <td className={`px-4 py-3 text-right font-semibold ${i === 0 ? 'text-teal-300' : 'text-white'}`}>
                    {cell(row.residentialMinor, row.increment)}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-white">
                    {cell(row.commercialMinor, row.increment)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <p className="mt-4 text-xs text-slate-500 leading-relaxed">
          {copy.commercialSurcharge} {SKETCHPLAN_PRICING_COPY.oneProperty} {SKETCHPLAN_PRICING_COPY.amendment} {sketchplanChargeSummary(currency)}
        </p>

        {onOrder && (
          <button
            type="button"
            onClick={onOrder}
            className="mt-5 inline-flex items-center justify-center px-5 py-3 bg-teal-600 hover:bg-teal-500 text-white rounded-xl text-sm font-semibold transition-all"
          >
            Start My Floor Plan
          </button>
        )}
      </div>
    </section>
  );
};

export default DirectCardPricingTable;
